import { Action, createReducer, on } from '@ngrx/store';
import filter from 'lodash/filter';
import { LoadStatus } from '@capital-access/common/utils';
import {
  ReportsTemplateListType,
  ReportTemplate,
  ReportTemplateFormType,
  ReportType,
  sortTemplateByDateDesc,
  TemplateLibraryState
} from '@capital-access/reporting/common';
import {
  changeActiveReportingTemplateListType,
  clearCreateReportSectionSettings,
  clearTemplateLibraryStore,
  createTemplateFailure,
  createTemplateSuccess,
  deleteTemplateSuccess,
  drawerAction,
  editTemplateFailure,
  editTemplateSuccess,
  loadAllTemplates,
  loadAllTemplatesFailure,
  loadAllTemplatesSuccess,
  openCreateReportingTemplateDrawer,
  openEditDuplicateReportingTemplateDrawer,
  openSelectedTemplate,
  resetCreateFeatureReducer,
  resetCreateReportForm,
  saveCreateReportForm,
  searchTextUpdate,
  setCombinedOwnershipEnabled,
  setNotesFeatureEnabled,
  setReportSetingSettings,
  setSelectedReportSectionsForPreview,
  setSelectedTemplateForPreview,
  submitReportForm,
  updateLandingPageReportData,
  updateReportForm
} from './actions';
const initialState: TemplateLibraryState = {
  originalTemplatesList: null,
  templatesList: null,
  loadingState: LoadStatus.Pristine,
  selectedReportType: null,
  deletedTemplateId: null,
  reportTemplateFormData: null,
  actionType: null,
  selectedTemplate: null,
  reportSections: [],
  saveDrawerLoadStatus: LoadStatus.Pristine,
  activeReportingTemplatesListType: ReportsTemplateListType.ALL,
  selectedTemplateForPreview: null,
  createReportSectionSettings: null,
  allowedTemplatesPerAccount: 60,
  templatePageSize: 'us-letter',
  reportData: {},
  searchValue: '',
  showCombinedOwnership: false,
  showNotesFeature: true
};
const reducer = createReducer(
  initialState,
  on(loadAllTemplates, (state, { silentRefresh = false }) => {
    if (silentRefresh) {
      return state;
    }
    return {
      ...state,
      loadingState: LoadStatus.Loading
    };
  }),
  on(loadAllTemplatesSuccess, (state, { data }) => {
    const sortedData = sortTemplateByDateDesc(data);
    let activeList = sortedData;
    if (state.activeReportingTemplatesListType === ReportsTemplateListType.MY_TEAMS) {
      activeList = filterByActiveTemplateListType(sortedData, ReportsTemplateListType.MY_TEAMS);
    } else if (state.activeReportingTemplatesListType === ReportsTemplateListType.PREDEFINED) {
      activeList = filterByActiveTemplateListType(sortedData, ReportsTemplateListType.PREDEFINED);
    }
    return {
      ...state,
      originalTemplatesList: sortedData,
      templatesList: activeList,
      loadingState: LoadStatus.Loaded
    };
  }),
  on(loadAllTemplatesFailure, state => ({
    ...state,
    loadingState: LoadStatus.Failed
  })),
  on(openCreateReportingTemplateDrawer, (state, { reportType, actionType, settings }) => ({
    ...state,
    selectedReportType: reportType,
    actionType,
    createReportSectionSettings: settings ?? null,
    saveDrawerLoadStatus: LoadStatus.Loaded // Initialize with Loaded state
  })),
  on(clearCreateReportSectionSettings, state => ({
    ...state,
    createReportSectionSettings: null
  })),
  on(openEditDuplicateReportingTemplateDrawer, (state, { template, actionType }) => ({
    ...state,
    selectedReportType: template.reportType as ReportType,
    actionType
  })),
  on(saveCreateReportForm, (state, { formData }) => ({
    ...state,
    reportTemplateFormData: formData,
    saveDrawerLoadStatus: LoadStatus.Loading
  })),
  on(submitReportForm, (state, { formData, actionType }) => ({
    ...state,
    reportTemplateFormData: formData,
    saveDrawerLoadStatus: actionType === ReportTemplateFormType.duplicate ? LoadStatus.Loaded : LoadStatus.Loading
  })),
  on(updateReportForm, (state, { formData }) => ({
    ...state,
    reportTemplateFormData: formData
  })),
  on(setSelectedTemplateForPreview, (state, { selectedTemplate }) => ({
    ...state,
    selectedTemplateForPreview: selectedTemplate
  })),
  on(changeActiveReportingTemplateListType, (state, { reportsListType }) => {
    return {
      ...state,
      searchValue: '',
      activeReportingTemplatesListType: reportsListType,
      templatesList: filterByActiveTemplateListType(state.originalTemplatesList, reportsListType)
    };
  }),
  on(editTemplateSuccess, (state, { templateId }) => ({
    ...state,
    saveDrawerLoadStatus: LoadStatus.Loaded,
    savedTemplateId: templateId
  })),
  on(createTemplateSuccess, (state, { templateId }) => ({
    ...state,
    saveDrawerLoadStatus: LoadStatus.Loaded,
    savedTemplateId: templateId
  })),
  on(editTemplateFailure, state => ({
    ...state,
    saveDrawerLoadStatus: LoadStatus.Failed
  })),
  on(deleteTemplateSuccess, state => {
    return {
      ...state,
      deletedTemplateId: state.selectedTemplateForPreview?.templateId
    };
  }),
  on(createTemplateFailure, state => ({
    ...state,
    saveDrawerLoadStatus: LoadStatus.Failed
  })),
  on(resetCreateReportForm, state => ({
    ...state,
    selectedReportType: null,
    reportTemplateFormData: null,
    createReportSectionSettings: null,
    actionType: null,
    selectedTemplate: null
  })),
  on(drawerAction, (state, { actionType }) => ({
    ...state,
    actionType: actionType
  })),
  on(openSelectedTemplate, (state, { data, reportType }) => ({
    ...state,
    selectedReportType: reportType,
    selectedTemplate: data
  })),
  on(
    setSelectedReportSectionsForPreview,
    (state, { createReportSectionSettings, reportSections, templatePageSize }) => ({
      ...state,
      createReportSectionSettings,
      reportSections,
      templatePageSize
    })
  ),
  on(setReportSetingSettings, (state, { createReportSectionSettings }) => ({
    ...state,
    createReportSectionSettings
  })),
  on(openEditDuplicateReportingTemplateDrawer, (state, { template }) => ({
    ...state,
    selectedReportType: template.reportType as ReportType
  })),
  on(updateLandingPageReportData, (state, { reportData }) => ({
    ...state,
    reportData: reportData
  })),
  on(clearTemplateLibraryStore, _ => ({
    ...initialState
  })),
  on(searchTextUpdate, (state, { searchText }) => {
    return {
      ...state,
      searchValue: searchText,
      templatesList: searchTemplatesFromText(
        state.originalTemplatesList,
        searchText,
        state.activeReportingTemplatesListType
      )
    };
  }),
  on(resetCreateFeatureReducer, () => ({
    ...initialState
  })),
  on(setCombinedOwnershipEnabled, (state, { enabled }) => ({
    ...state,
    showCombinedOwnership: enabled
  })),
  on(setNotesFeatureEnabled, (state, { enabled }) => ({
    ...state,
    showNotesFeature: enabled
  }))
);
function searchTemplatesFromText(
  originalTemplates: ReportTemplate[] | null,
  searchText: string,
  activeTemplateListType: ReportsTemplateListType
): ReportTemplate[] {
  const templatesToBeSearchedFor = filterByActiveTemplateListType(originalTemplates, activeTemplateListType);
  if (searchText === '') {
    return templatesToBeSearchedFor;
  }
  return sortTemplateByDateDesc(
    filter(templatesToBeSearchedFor, (template: ReportTemplate) => {
      return template.templateName.toLowerCase().includes(searchText.toLowerCase());
    })
  );
}
function filterByActiveTemplateListType(
  templates: ReportTemplate[] | null,
  activeTemplateListType: ReportsTemplateListType
): ReportTemplate[] {
  return sortTemplateByDateDesc(
    filter(templates || [], (template: ReportTemplate) => {
      if (activeTemplateListType === ReportsTemplateListType.PREDEFINED) {
        return template.caTemplateInd;
      } else if (activeTemplateListType === ReportsTemplateListType.MY_TEAMS) {
        return !template.caTemplateInd;
      }
      return true;
    })
  );
}
export function templateLibraryReducer(state: TemplateLibraryState | undefined, action: Action) {
  return reducer(state, action);
}
export const getInitialState = () => initialState;
